import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Clock, CheckCircle, XCircle, Trash2, Search, Filter, AlertCircle } from 'lucide-react';
import { Card } from '../ui/card';
import { Input } from '../ui/input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { API_URL } from '../../lib/api-client';
import { PaginationWrapper } from '../ui/PaginationWrapper';

interface DeleteRequest {
  _id: string;
  userId: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    role: string;
    profileCompleted: boolean;
  };
  userName: string;
  userEmail: string;
  userPhone: string;
  reason: string;
  status: 'pending' | 'approved' | 'rejected';
  processedBy?: {
    firstName: string;
    lastName: string;
    email: string;
  };
  processedAt?: string;
  adminNotes?: string;
  rejectionReason?: string;
  createdAt: string;
}

interface Stats {
  pending: number;
  approved: number;
  rejected: number;
  total: number;
}

export default function DeleteAccountRequests() {
  const [requests, setRequests] = useState<DeleteRequest[]>([]);
  const [stats, setStats] = useState<Stats>({ pending: 0, approved: 0, rejected: 0, total: 0 });
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedRequest, setSelectedRequest] = useState<DeleteRequest | null>(null);
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [adminNotes, setAdminNotes] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');

  useEffect(() => {
    loadStats();
    loadRequests();
  }, [statusFilter, page, search]);

  const loadStats = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/delete-account/admin/stats`);
      if (data.success) {
        setStats(data.data);
      }
    } catch (error: any) {
      console.error('Load stats error:', error);
    }
  };

  const loadRequests = async () => {
    try {
      setLoading(true);
      const params: any = {
        page,
        limit: 20
      };

      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }

      if (search.trim()) {
        params.search = search.trim();
      }

      const { data } = await axios.get(`${API_URL}/delete-account/admin/all`, { params });
      
      if (data.success) {
        setRequests(data.data.requests);
        setTotalPages(data.data.pagination.totalPages);
      }
    } catch (error: any) {
      console.error('Load requests error:', error);
      toast.error('Failed to load delete requests');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!selectedRequest) return;

    try {
      setLoading(true);
      const { data } = await axios.put(
        `${API_URL}/delete-account/admin/${selectedRequest._id}/approve`,
        { adminNotes }
      );

      if (data.success) {
        toast.success('Delete request approved. Account has been deleted.');
        setShowApproveDialog(false);
        setAdminNotes('');
        setSelectedRequest(null);
        loadStats();
        loadRequests();
      }
    } catch (error: any) {
      console.error('Approve error:', error);
      toast.error(error.response?.data?.message || 'Failed to approve request');
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async () => {
    if (!selectedRequest || !rejectionReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }

    try {
      setLoading(true);
      const { data } = await axios.put(
        `${API_URL}/delete-account/admin/${selectedRequest._id}/reject`,
        { rejectionReason, adminNotes }
      );

      if (data.success) {
        toast.success('Delete request rejected');
        setShowRejectDialog(false);
        setRejectionReason('');
        setAdminNotes('');
        setSelectedRequest(null);
        loadStats();
        loadRequests();
      }
    } catch (error: any) {
      console.error('Reject error:', error);
      toast.error(error.response?.data?.message || 'Failed to reject request');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-yellow-100 text-yellow-800">
          <Clock className="size-3" />
          Pending
        </span>;
      case 'approved':
        return <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-green-100 text-green-800">
          <CheckCircle className="size-3" />
          Approved
        </span>;
      case 'rejected':
        return <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-red-100 text-red-800">
          <XCircle className="size-3" />
          Rejected
        </span>;
      default:
        return null;
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl">Delete Account Requests</h1>
          <p className="text-gray-600 mt-1">Review and manage user deletion requests</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Pending</p>
              <p className="text-2xl mt-1">{stats.pending}</p>
            </div>
            <div className="p-3 bg-yellow-100 rounded-lg">
              <Clock className="size-6 text-yellow-600" />
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Approved</p>
              <p className="text-2xl mt-1">{stats.approved}</p>
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <CheckCircle className="size-6 text-green-600" />
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Rejected</p>
              <p className="text-2xl mt-1">{stats.rejected}</p>
            </div>
            <div className="p-3 bg-red-100 rounded-lg">
              <XCircle className="size-6 text-red-600" />
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total</p>
              <p className="text-2xl mt-1">{stats.total}</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
              <Trash2 className="size-6 text-blue-600" />
            </div>
          </div>
        </Card>
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-gray-400" />
              <Input
                placeholder="Search by name, email, or phone..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
          
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full md:w-48">
              <Filter className="size-4 mr-2" />
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Requests</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Card>

      {/* Requests Table */}
      <Card>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-6 py-3 text-left text-xs uppercase tracking-wider text-gray-700">User</th>
                <th className="px-6 py-3 text-left text-xs uppercase tracking-wider text-gray-700">Contact</th>
                <th className="px-6 py-3 text-left text-xs uppercase tracking-wider text-gray-700">Reason</th>
                <th className="px-6 py-3 text-left text-xs uppercase tracking-wider text-gray-700">Status</th>
                <th className="px-6 py-3 text-left text-xs uppercase tracking-wider text-gray-700">Date</th>
                <th className="px-6 py-3 text-left text-xs uppercase tracking-wider text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-6 py-8 text-center text-gray-500">
                    Loading requests...
                  </td>
                </tr>
              ) : requests.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-8 text-center text-gray-500">
                    No delete requests found
                  </td>
                </tr>
              ) : (
                requests.map((request) => (
                  <tr key={request._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div>
                        <p>{request.userName}</p>
                        {request.userId && (
                          <p className="text-xs text-gray-500 mt-1">
                            {request.userId.role} • {request.userId.profileCompleted ? 'Complete' : 'Incomplete'}
                          </p>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm">
                        <p>{request.userEmail}</p>
                        <p className="text-gray-500">{request.userPhone}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-sm max-w-xs truncate">{request.reason}</p>
                    </td>
                    <td className="px-6 py-4">
                      {getStatusBadge(request.status)}
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-sm">{new Date(request.createdAt).toLocaleDateString()}</p>
                      <p className="text-xs text-gray-500">{new Date(request.createdAt).toLocaleTimeString()}</p>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        {request.status === 'pending' && (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setSelectedRequest(request);
                                setShowApproveDialog(true);
                              }}
                              className="text-green-600 hover:text-green-700"
                            >
                              <CheckCircle className="size-4 mr-1" />
                              Approve
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setSelectedRequest(request);
                                setShowRejectDialog(true);
                              }}
                              className="text-red-600 hover:text-red-700"
                            >
                              <XCircle className="size-4 mr-1" />
                              Reject
                            </Button>
                          </>
                        )}
                        {request.status !== 'pending' && request.processedBy && (
                          <div className="text-xs text-gray-500">
                            Processed by {request.processedBy.firstName} {request.processedBy.lastName}
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <PaginationWrapper
            currentPage={page}
            totalPages={totalPages}
            onPageChange={setPage}
          />
        )}
      </Card>

      {/* Approve Dialog */}
      <Dialog open={showApproveDialog} onOpenChange={setShowApproveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Approve Delete Request</DialogTitle>
            <DialogDescription>
              This will permanently delete the user's account. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>

          {selectedRequest && (
            <div className="space-y-4">
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertCircle className="size-5 text-yellow-600 mt-0.5" />
                  <div>
                    <p className="text-sm">
                      <span className="font-medium">{selectedRequest.userName}</span> ({selectedRequest.userEmail})
                    </p>
                    <p className="text-sm text-gray-600 mt-1">Reason: {selectedRequest.reason}</p>
                  </div>
                </div>
              </div>

              <div>
                <Label htmlFor="adminNotes">Admin Notes (Optional)</Label>
                <Textarea
                  id="adminNotes"
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  placeholder="Add any internal notes..."
                  className="mt-2"
                  rows={3}
                />
              </div>

              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowApproveDialog(false);
                    setAdminNotes('');
                    setSelectedRequest(null);
                  }}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleApprove}
                  disabled={loading}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  {loading ? 'Processing...' : 'Approve & Delete Account'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Delete Request</DialogTitle>
            <DialogDescription>
              Provide a reason for rejecting this delete request.
            </DialogDescription>
          </DialogHeader>

          {selectedRequest && (
            <div className="space-y-4">
              <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                <p className="text-sm">
                  <span className="font-medium">{selectedRequest.userName}</span> ({selectedRequest.userEmail})
                </p>
                <p className="text-sm text-gray-600 mt-1">Reason: {selectedRequest.reason}</p>
              </div>

              <div>
                <Label htmlFor="rejectionReason">Rejection Reason *</Label>
                <Textarea
                  id="rejectionReason"
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Explain why this request is being rejected..."
                  className="mt-2"
                  rows={3}
                  required
                />
              </div>

              <div>
                <Label htmlFor="adminNotesReject">Admin Notes (Optional)</Label>
                <Textarea
                  id="adminNotesReject"
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  placeholder="Add any internal notes..."
                  className="mt-2"
                  rows={2}
                />
              </div>

              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowRejectDialog(false);
                    setRejectionReason('');
                    setAdminNotes('');
                    setSelectedRequest(null);
                  }}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleReject}
                  disabled={loading || !rejectionReason.trim()}
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  {loading ? 'Processing...' : 'Reject Request'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}